import React, {useEffect, useMemo, useState} from "react";
import styles from "./SubscribeToAlertsModal.module.scss";

import {ApplicationContext, ch} from "@renta-apps/athenaeum-react-common";
import {Modal, ModalSize, Spinner} from "@renta-apps/athenaeum-react-components";
import {Button, ButtonType, Checkbox, CHECKBOX_PARTIAL} from '@renta-apps/renta-react-components';

import {useResize} from "@/helpers/Hooks";
import {validatePhoneNumber} from "@/helpers/Validators";
import Localizer from "@/localization/Localizer";
import {AlarmMediaType} from "@/models/Enums";
import UserContext from "@/models/server/UserContext";
import {deleteAlarmAsync, subscribeToAlert} from "@/services/AlarmService";
import {saveUserAlarmMediaType} from "@/services/UserService";
import {IDeletedAlarm, INewAlarm} from "../Devices";
import {selectedDevicesInitializer, useAlertsSubscriptionsManagementState} from "@/components/AlertSubscriptionsManagement/useAlertsSubscriptionsManagementState";
import AlertSubscriptionsManagement, {SubscriptionAlertConfig} from "@/components/AlertSubscriptionsManagement/AlertSubscriptionsManagement";
import BaseAlarmDeviceModel from "@/models/BaseAlarmDeviceModel";
import BaseAlarmModel from "@/models/BaseAlarmModel";
import {AlarmTypeHelper} from "@/helpers/AlarmTypeHelper";

type SubscribeToAlertsModalProps = {
    isOpen: boolean;
    selectedDevices: BaseAlarmDeviceModel<BaseAlarmModel>[];
    onClose(): void;
    updateDevicesSubscriptions: (newAlarms: INewAlarm[], deletedAlarms: IDeletedAlarm[], alertsConfig: SubscriptionAlertConfig[]) => void;
}

const SubscribeToAlertsModal: React.FC<SubscribeToAlertsModalProps> = ({isOpen, selectedDevices, onClose, updateDevicesSubscriptions}) => {
    const _modalRef: React.RefObject<Modal> = React.createRef();

    const [
        alertsConfig,
        updateSubscription,
        updateAlertData,
        updateAdjustmentState,
        setSubscriptions,
    ] = useAlertsSubscriptionsManagementState(null);

    const [isSmsNotificationChecked, setIsSmsNotificationChecked] = useState<boolean>(false);
    const [isEmailNotificationChecked, setIsEmailNotificationChecked] = useState<boolean>(false);

    const [isBeingSaved, setIsBeingSaved] = useState(false);
    const {windowWidth} = useResize();

    const context: ApplicationContext = ch.getContext();
    const userContext = context as UserContext;

    const {alarmMediaType, constructionSiteId, contractId, organizationId, isPhoneNumberNumberInvalid} = useMemo(() => {
        return {
            alarmMediaType: userContext.user?.alarmMediaType ?? null,
            constructionSiteId: userContext.selectedRole?.constructionSiteId ?? null,
            contractId: userContext.selectedRole?.contractId ?? null,
            organizationId: userContext.selectedRole?.organization?.id ?? null,
            isPhoneNumberNumberInvalid: !!validatePhoneNumber(userContext.user?.phoneNumber ?? null),
        };
    }, [userContext]);

    useEffect(() => {
        setAlarmType(alarmMediaType);
    }, [alarmMediaType]);

    const {singleDevice, numberOfDevices} = useMemo(() => {
        if (!isOpen) {
            return {singleDevice: false, numberOfDevices: 0};
        }

        return {
            singleDevice: selectedDevices.length === 1,
            numberOfDevices: selectedDevices.length
        };
    }, [selectedDevices, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setSubscriptions(null);
            return;
        }

        setSubscriptions(() => selectedDevicesInitializer(selectedDevices));
    }, [selectedDevices, isOpen]);

    useEffect(() => {
        if (isOpen) {
            _modalRef.current?.openAsync();
        } else {
            _modalRef.current?.closeAsync();
        }
    }, [_modalRef, isOpen]);

    const handleNotificationSettings = async (): Promise<void> => {
        if (isSmsNotificationChecked && isEmailNotificationChecked) {
            await saveUserAlarmMediaType(AlarmMediaType.EmailAndSms);
        } else if (isSmsNotificationChecked) {
            await saveUserAlarmMediaType(AlarmMediaType.Sms);
        } else if (isEmailNotificationChecked) {
            await saveUserAlarmMediaType(AlarmMediaType.Email);
        } else {
            await saveUserAlarmMediaType(AlarmMediaType.None);
        }
    };

    const saveChangesAsync = async () => {
        setIsBeingSaved(true);

        const newAlarms: INewAlarm[] = [];
        const deletedAlarms: IDeletedAlarm[] = [];

        for (const device of selectedDevices) {
            for (const alertType of AlarmTypeHelper.selectedDevicesConfig) {
                if (!alertType.supported(device)) {
                    continue;
                }

                const {enabled, isAdjustmentEnabled, alarmData} = alertsConfig!.find(alert => alert.type === alertType.type)!;
                const configuredAlert = alertType.getConfiguredAlert(device);
                const newAlarmData = JSON.stringify(alarmData);
                const currentAlarmData = JSON.stringify(configuredAlert?.alarmData);
                const alarmType = AlarmTypeHelper.getAlarmTypeBySubscriptionType(device.supportedAlarmTypes, alertType.type);

                const createOrUpdateAlarm = async () => {
                    const newAlarm = await subscribeToAlert(alertType.type, alarmType, device, constructionSiteId, contractId, organizationId, alarmData);
                    if (newAlarm) {
                        newAlarms.push({configuredAlert: newAlarm, deviceId: device.rentaId});
                    }
                };

                const deleteAndCreateAlarm = async () => {
                    await deleteAlarmAsync(configuredAlert!.id);
                    deletedAlarms.push({alarmId: configuredAlert!.id, deviceId: device.rentaId});
                    await createOrUpdateAlarm();
                };

                if (enabled === true) {
                    if (!configuredAlert) {
                        await createOrUpdateAlarm();
                    } else if (configuredAlert && alertType.supportsUpdate && isAdjustmentEnabled && newAlarmData !== currentAlarmData) {
                        await createOrUpdateAlarm();
                    } else if (configuredAlert && isAdjustmentEnabled && newAlarmData !== currentAlarmData) {
                        // When the alert is still enabled but the alarmData has changed, we need to delete the old alarm and create a new one.
                        await deleteAndCreateAlarm();
                    }
                } else if (enabled === CHECKBOX_PARTIAL && configuredAlert && isAdjustmentEnabled && newAlarmData !== currentAlarmData) {
                    // When the alert is in partial state but the alarmData has changed, we need to delete the old alarm and create a new one.
                    await deleteAndCreateAlarm();
                } else if (enabled === false && configuredAlert) {
                    await deleteAlarmAsync(configuredAlert.id);
                    deletedAlarms.push({alarmId: configuredAlert.id, deviceId: device.rentaId});
                }
            }
        }

        await handleNotificationSettings();

        updateDevicesSubscriptions(newAlarms, deletedAlarms, alertsConfig!);

        setIsBeingSaved(false);
        onClose();
    };

    const setAlarmType = (alarmMediaType: AlarmMediaType | null) => {
        switch (alarmMediaType) {
            case AlarmMediaType.Email:
                setIsEmailNotificationChecked(true);
                setIsSmsNotificationChecked(false);
                break;
            case AlarmMediaType.Sms:
                setIsEmailNotificationChecked(false);
                setIsSmsNotificationChecked(true);
                break;
            case AlarmMediaType.EmailAndSms:
                setIsEmailNotificationChecked(true);
                setIsSmsNotificationChecked(true);
                break;
            default:
                setIsEmailNotificationChecked(false);
                setIsSmsNotificationChecked(false);
                break;
        }
    };

    return (
        isOpen ? (
            <Modal
                preventEsc preventClosingOnOutsideClick preventClosingOnInsideClick
                ref={_modalRef}
                id="subscribeToAlertsModal"
                size={ModalSize.Auto}
                title={Localizer.fleetMonitoringPageSubscribeToAlertsModalTitle.toUpperCase()}
                className={styles.modal}
                notResponsive={windowWidth > 480}
                onClose={async () => onClose()}
            >
                <div className={styles.mainContainer}>
                    <div className={`${styles.activateAlerts} ${isBeingSaved ? styles.transparentForm : ''}`}>
                        <div className={styles.titleArea}>
                            {singleDevice ? (
                                <span className={styles.title}>
                                    {Localizer.fleetMonitoringPageSubscribeToAlertsModalHeaderTextPieceOfEquipment}
                                </span>
                            ) : (
                                <>
                                    <span className={styles.title}>
                                        {Localizer.fleetMonitoringPageSubscribeToAlertsModalHeaderTextSelectedEquipment}
                                    </span>
                                    <span className={styles.subTitle}>
                                        {Localizer.fleetMonitoringPageSubscribeToAlertsModalSubHeaderText}
                                    </span>
                                </>
                            )}
                        </div>
                        {alertsConfig && numberOfDevices > 0 && (
                            <AlertSubscriptionsManagement disabled={isBeingSaved}
                                                          singleDevice={singleDevice}
                                                          numberOfDevices={numberOfDevices}
                                                          alerts={alertsConfig!}
                                                          onChange={updateSubscription}
                                                          onAlertDataChange={updateAlertData}
                                                          onAdjustmentChange={updateAdjustmentState}
                            />
                        )}
                    </div>

                    <div className={`${styles.alertsSettings} ${isBeingSaved ? styles.transparentForm : ''}`}>
                        <div className={styles.alertsSettingsTitle}>
                            <span className={styles.title}>{Localizer.fleetMonitoringPageSubscribeToAlertsModalGlobalAlertSettings}</span>
                            <span className={styles.subTitle}>{Localizer.fleetMonitoringPageSubscribeToAlertsModalChangingSettings}</span>
                        </div>
                        <div className={styles.formFields}>
                            <div className={styles.checkboxNotification}>
                                <Checkbox
                                    id="smsNotifications"
                                    label={Localizer.fleetMonitoringPageSubscribeToAlertsModalReceiveSms}
                                    checked={isSmsNotificationChecked}
                                    onChange={(value: boolean) => setIsSmsNotificationChecked(value)}
                                    disabled={isPhoneNumberNumberInvalid}
                                />
                                {isPhoneNumberNumberInvalid && <div className={styles.invalidPhoneNumber}>{Localizer.fleetMonitoringPageSubscribeToAlertsModalInvalidPhoneNumber}</div>}
                            </div>
                            <div className={styles.checkboxNotification}>
                                <Checkbox
                                    id="emailNotifications"
                                    label={Localizer.fleetMonitoringPageSubscribeToAlertsModalReceiveEmail}
                                    checked={isEmailNotificationChecked}
                                    onChange={(value: boolean) => setIsEmailNotificationChecked(value)}
                                />
                            </div>
                        </div>
                    </div>

                    {!isBeingSaved && <div id="subscribeToAlertsModalActionButtons" className={styles.activeButtons}>
                        <Button type={ButtonType.Secondary} onClick={() => {
                            onClose();
                        }}>{Localizer.componentModalCancel}</Button>
                        <Button type={ButtonType.Primary} onClick={async () => {
                            await saveChangesAsync();
                        }}>{Localizer.componentModalSaveChanges}</Button>
                    </div>
                    }

                    {isBeingSaved && <div id="subscribeToAlertsModalActionLoader" className={styles.activeButtons}>
                        <div className={styles.spinnerContainer}>
                            <Spinner noShading/>
                        </div>
                        <div className={styles.textContainer}>
                            <span>{Localizer.fleetMonitoringPageSubscribeToAlertsModalPleaseWait}</span>
                        </div>
                    </div>
                    }
                </div>
            </Modal>
        ) : null
    );
};

export default SubscribeToAlertsModal;